
import { useState } from "react"
import { LoadingSpinner } from "@/components/ui/LoadingSpinner"
import { Plus, Search, Filter, Calendar, DollarSign, Users, MapPin, IndianRupee } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { EventForm } from "@/components/events/EventForm"
import { useEvents } from "@/hooks/useEvents"
import { useBrands } from "@/hooks/useBrands"
import { useCampaigns } from "@/hooks/useCampaigns"
import { formatCurrency } from "@/lib/utils/currency"
import { format } from "date-fns"
import { useNavigate } from "react-router-dom"

const eventTypes = [
  "Product Launch",
  "Pop-up",
  "Party",
  "Workshop",
  "Press Meet",
  "Networking",
  "Conference"
]

export default function Events() {
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedType, setSelectedType] = useState("")
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const navigate = useNavigate()

  const { data: events, refetch: refetchEvents, isLoading } = useEvents()
  const { data: brands } = useBrands()
  const { data: campaigns } = useCampaigns()

  const filteredEvents = (events || []).filter(event => {
    const matchesSearch =
      event.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.event_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (getBrandName(event.brand_id) || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (getCampaignName(event.campaign_id) || "").toLowerCase().includes(searchTerm.toLowerCase())

    const matchesType = selectedType === "" || event.event_type === selectedType
    return matchesSearch && matchesType
  })

  const getBrandName = (brandId: string) => {
    return brands?.find(b => b.id === brandId)?.name || "N/A"
  }

  const getCampaignName = (campaignId: string) => {
    return campaigns?.find(c => c.id === campaignId)?.name || "N/A"
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "upcoming": return "bg-blue-100 text-blue-800"
      case "completed": return "bg-green-100 text-green-800"
      case "planning": return "bg-yellow-100 text-yellow-800"
      case "cancelled": return "bg-red-100 text-red-800"
      default: return "bg-gray-100 text-gray-800"
    }
  }

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "MMM dd, yyyy")
  }

  const totalBudget = events?.reduce(
    (sum, e) => sum + (e.total_budget || 0), 0
  ) || 0

  const upcomingEvents = events?.filter(e =>
    new Date(e.event_date) > new Date() && e.status !== "cancelled"
  ).length || 0

  if (isLoading) {
    return <LoadingSpinner size="fullscreen" />
  }

  return (
    <div className="min-h-screen">
      <div className="container mx-auto p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Events Management</h1>
            <p className="text-gray-600">Plan and manage your brand events and activations</p>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg hover:from-purple-700 hover:to-blue-700 text-white shadow-lg"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Event
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-white shadow-lg border-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Events</CardTitle>
              <Calendar className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{events?.length || 0}</div>
              <p className="text-xs text-gray-500">Active events</p>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg border-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Budget</CardTitle>
              <IndianRupee className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{formatCurrency(totalBudget, "IN")}</div>
              <p className="text-xs text-gray-500">Across all events</p>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg border-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Upcoming Events</CardTitle>
              <Users className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{upcomingEvents}</div>
              <p className="text-xs text-gray-500">Events scheduled</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6 bg-white shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search events, brands, or campaigns..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="w-full md:w-48">
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="">All Event Types</option>
                  {eventTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Events Table */}
        <Card className="bg-white shadow-lg border-0">
          <CardHeader>
            <CardTitle>Events ({filteredEvents.length})</CardTitle>
            <CardDescription>Manage your upcoming and past events</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Event Name</TableHead>
                  <TableHead>Date & Time</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>Budget</TableHead>
                  <TableHead>Spent</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Venue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEvents.map(event => (
                  <TableRow
                    key={event.id}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <TableCell className="font-medium">
                      <div 
                        className="font-semibold text-blue-600 hover:text-blue-800 cursor-pointer"
                        onClick={() => navigate(`/events/${event.id}`)}
                      >
                        {event.name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Calendar className="h-3 w-3 text-gray-500" />
                        <span>{formatDate(event.event_date)}</span>
                      </div>
                      {event.event_time && (
                        <div className="text-xs text-gray-500">{event.event_time}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {event.event_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {getCampaignName(event.campaign_id)}
                    </TableCell>
                    <TableCell className="text-sm font-medium text-gray-900">
                      {getBrandName(event.brand_id)}
                    </TableCell>
                    <TableCell className="text-sm font-medium text-green-600">
                      {formatCurrency(event.total_budget || 0, "IN")}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {formatCurrency(event.total_spent || 0, "IN")}
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-xs ${getStatusColor(event.status)}`}>
                        {event.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <MapPin className="h-3 w-3" />
                        <span className="truncate max-w-32">
                          {event.venue_address || "-"}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* New Event Modal */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl border-0 shadow-2xl">
          <DialogHeader className="pb-6 border-b border-gray-100">
            <DialogTitle className="text-2xl font-bold text-gray-900">Create New Event</DialogTitle>
            <p className="text-gray-600 mt-1">
              Plan your next brand event or activation with all the details
            </p>
          </DialogHeader>
          <EventForm
            onSuccess={() => {
              setShowCreateDialog(false)
              refetchEvents()
            }}
            onCancel={() => setShowCreateDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}
